import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import heroSlide13 from '../file/original/slider-13.png';
import heroSlide10 from '../file/original/slider-10.png';
import heroSlide11 from '../file/original/slider-11.png';
import heroLoopVideo from '../file/DrsFarmループ.mp4';
import placeholderFarmerImage from '../file/farmer/画像なし農家.jpeg';
import useScrollReveal from '../hooks/useScrollReveal';
import {
  getHeaderTickerConfig,
  getResolvedHeaderTickerData,
  listPublicFarmerReports,
  listPublicOperatorAnnouncements,
  listPublicPosts,
} from '../lib/firestore';
import { normalizeInstagramUrl } from '../lib/social';
import InstagramPreview from '../components/InstagramPreview';
import { mergeFeaturedFarmers } from '../data/farmers';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import Seo from '../components/Seo';
import { toAbsoluteUrl } from '../lib/seo';

const flowSteps = [
  {
    id: 1,
    title: '参加農家を知る',
    body: '地域・作物・取り組みから、気になる農家を見つけます。',
  },
  {
    id: 2,
    title: '現場の考え方を読む',
    body: '土づくりや農薬に頼らない工夫など、日々の判断の背景を知ります。',
  },
  {
    id: 3,
    title: '記録と検査を確かめる',
    body: '栽培記録や土壌分析など、根拠となる情報を確認します。',
  },
  {
    id: 4,
    title: '医療の視点で理解する',
    body: '糖尿病など生活習慣病や子どもの発達も含め、食が健康に与える影響を医師の言葉で整理します。',
  },
  {
    id: 5,
    title: '記事で変化を追う',
    body: '季節ごとの現場の変化や学びを、記事で継続的に追えます。',
  },
  {
    id: 6,
    title: 'チームに相談する',
    body: '医師・農家・運営に、疑問や連携の相談ができます。',
  },
  {
    id: 7,
    title: '日々の選択に活かす',
    body: '得た情報を、家族の食材選びや未病予防につなげます。',
  },
];

export default function HomePage() {
  const instagramUrl = normalizeInstagramUrl(import.meta.env.VITE_INSTAGRAM_PROFILE_URL);
  const [searchParams] = useSearchParams();
  const [farmers, setFarmers] = useState([]);
  const [collaborationItems, setCollaborationItems] = useState([]);
  const [loadingFarmers, setLoadingFarmers] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [debugSnapshot, setDebugSnapshot] = useState(null);
  useScrollReveal();
  const isDebugMode = searchParams.get('debug') === '1';

  useEffect(() => {
    setFarmers(mergeFeaturedFarmers([]));
    setLoadingFarmers(false);

    listPublicOperatorAnnouncements(6)
      .then(setCollaborationItems)
      .catch(() => setCollaborationItems([]))
      .finally(() => setLoadingPosts(false));
  }, []);

  useEffect(() => {
    if (!isDebugMode) {
      setDebugSnapshot(null);
      return;
    }

    let active = true;

    async function runDebug() {
      try {
        const [
          resolvedResult,
          publicConfigResult,
          adminConfigResult,
          rawHeaderConfigResult,
          publicPostsResult,
          publicReportsResult,
        ] = await Promise.allSettled([
          getResolvedHeaderTickerData({ force: true }),
          getDoc(doc(db, 'terms', 'headerTickerPublic')).then((snap) => ({
            exists: snap.exists(),
            data: snap.exists() ? snap.data() : null,
          })),
          getDoc(doc(db, 'siteSettings', 'headerTicker')).then((snap) => ({
            exists: snap.exists(),
            data: snap.exists() ? snap.data() : null,
          })),
          getHeaderTickerConfig(),
          listPublicPosts(),
          listPublicFarmerReports(),
        ]);

        const resolved = resolvedResult.status === 'fulfilled'
          ? resolvedResult.value
          : null;

        const snapshot = {
          checkedAt: new Date().toISOString(),
          currentUser: auth.currentUser
            ? { uid: auth.currentUser.uid, email: auth.currentUser.email || '' }
            : null,
          resolved: resolvedResult.status === 'fulfilled'
            ? resolvedResult.value
            : { error: resolvedResult.reason?.code || resolvedResult.reason?.message || String(resolvedResult.reason) },
          publicConfig: publicConfigResult.status === 'fulfilled'
            ? publicConfigResult.value
            : { error: publicConfigResult.reason?.code || publicConfigResult.reason?.message || String(publicConfigResult.reason) },
          adminConfig: adminConfigResult.status === 'fulfilled'
            ? adminConfigResult.value
            : { error: adminConfigResult.reason?.code || adminConfigResult.reason?.message || String(adminConfigResult.reason) },
          rawHeaderConfig: rawHeaderConfigResult.status === 'fulfilled'
            ? rawHeaderConfigResult.value
            : { error: rawHeaderConfigResult.reason?.code || rawHeaderConfigResult.reason?.message || String(rawHeaderConfigResult.reason) },
          publicPosts: publicPostsResult.status === 'fulfilled'
            ? { count: publicPostsResult.value.length, items: publicPostsResult.value }
            : { error: publicPostsResult.reason?.code || publicPostsResult.reason?.message || String(publicPostsResult.reason) },
          publicReports: publicReportsResult.status === 'fulfilled'
            ? { count: publicReportsResult.value.length, items: publicReportsResult.value }
            : { error: publicReportsResult.reason?.code || publicReportsResult.reason?.message || String(publicReportsResult.reason) },
        };

        console.log('[debug/header-ticker] snapshot', snapshot);
        console.log('[debug/header-ticker] resolved.items', resolved?.items || []);
        console.log('[debug/header-ticker] resolved.allItems', resolved?.allItems || []);
        console.log('[debug/header-ticker] publicPosts', snapshot.publicPosts);
        console.log('[debug/header-ticker] publicReports', snapshot.publicReports);

        if (active) {
          setDebugSnapshot(snapshot);
        }
      } catch (error) {
        console.error('[debug/header-ticker] failed', error);
        if (active) {
          setDebugSnapshot({
            checkedAt: new Date().toISOString(),
            error: error?.message || String(error),
          });
        }
      }
    }

    runDebug();
    return () => {
      active = false;
    };
  }, [isDebugMode]);

  const featuredFarmer = useMemo(() => {
    const availableFarmers = farmers.filter(
      (row) => row?.heroImageUrl && row.heroImageUrl !== placeholderFarmerImage,
    );

    if (!availableFarmers.length) return null;
    return availableFarmers[Math.floor(Math.random() * availableFarmers.length)];
  }, [farmers]);
  return (
    <>
      <Seo
        description="食が健康をつくる。糖尿病専門医の視点と、農薬に頼らない農家の実践をつなぎ、未病予防と地域の健康づくりを進めるDrs.Farm。"
        path="/"
        image={toAbsoluteUrl('/favicon.png')}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Drs.Farm',
          url: toAbsoluteUrl('/'),
          description: '医師の視点と生産現場の知恵をつなぎ、食の信頼を積み上げるプロジェクトです。',
        }}
      />

      <section className="story-scroll full-bleed">
        <div className="story-sticky">
          <video
            className="story-video"
            src={heroLoopVideo}
            poster={heroSlide13}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            aria-label="Drs.Farmの背景動画"
          />
          <div className="story-hole-layer" aria-hidden="true" />
        </div>
        <div className="story-lines">
          <section className="story-message story-message-left reveal" data-reveal>
            <div className="story-message-inner">
              <p className="eyebrow">Drs. Farm Story</p>
              <h2 className="story-main-title">食が健康をつくる。<br/>医師と農家が連携し、<br/>健康な毎日へ</h2>
              <div className="hero-actions">
                <Link to="/farmers" className="btn btn-primary">農家を見る</Link>
                <Link to="/doctors" className="btn btn-secondary">ドクターを見る</Link>
              </div>
            </div>
          </section>
          <section className="story-message story-message-right reveal" data-reveal>
            <div className="story-message-inner">
              <p className="eyebrow">Message</p>
              <h3>農薬に頼らない実践を、<br/>継続して応援したい。</h3>
              <p>現場の努力が正しく伝わり、選ぶ人が増える仕組みをつくります。</p>
            </div>
          </section>
          <section className="story-message story-message-left reveal" data-reveal>
            <div className="story-message-inner">
              <h3>医師として、<br/>食の力を何度も見てきた。</h3>
              <p>
                生活習慣病だけでなく、子どもの発達にも食は影響します。<br/>
                まずは“知る”ことから始めたいと考えています。
              </p>
            </div>
          </section>
          <section className="story-message story-message-right reveal" data-reveal>
            <div className="story-message-inner">
              <h3>医師・看護師・栄養士、<br/>そして家族とつながる。</h3>
              <p>若いお母さんや子どもたちにも届く形で、<br/>地域全体の健康づくりへ広げていきます。</p>
            </div>
          </section>
        </div>
      </section>

      <section className="editorial-section reveal" data-reveal>
        <div className="editorial-text">
          <p className="eyebrow">Statement</p>
          <h2>医師の視点と農家の実践を、
            <br />ひとつのストーリーとして届ける。</h2>
          <p>
            食は、体だけでなく日々の調子や行動にも影響します。<br/>
            だからこそDrs.Farmは、農家の実践（現場）と医療の視点（根拠）をひとつの流れで届けます。<br/>
            高価に感じられる野菜であっても、その価値が納得できる形で伝わることを大切にしています。
          </p>
          <Link to="/doctors" className="btn btn-secondary">私たちについて</Link>
        </div>
      </section>

      <section className="numbered-story reveal" data-reveal>
        <div className="numbered-story-media">
          <img src={heroSlide10} alt="農家の作業風景" loading="lazy" />
        </div>
        <div className="numbered-story-copy">
          <p className="number-label">01 FARMERS STORY</p>
          <h2>参加農家の現場から、
            <br />食卓に届くまでを読む。</h2>
          <p>
            栽培哲学、土づくり、農薬に頼らない工夫、日々の変化。<br/>
            現場の判断が見える形で、短い言葉と写真でたどれます。
          </p>
          <Link to="/visibility" className="text-btn">ここから読む</Link>
        </div>
      </section>

      <section className="audience-split reveal" data-reveal>
        <article className="audience-panel">
          <p className="audience-kicker">For People</p>
          <h3>日々の食事を見直したい方へ</h3>
          <p>農家・医師・記事を横断して、家族の食選びに使える「安心の材料」を集められます。</p>
          <Link to="/farmers" className="btn btn-secondary">参加農家を見る</Link>
        </article>
        <article className="audience-panel">
          <p className="audience-kicker">For Partners</p>
          <h3>連携したい医師・事業者の方へ</h3>
          <p>医師・看護師・栄養士・事業者など、食と健康に関わる連携相談を受け付けています。</p>
          <a href="mailto:drs.farm.info@gmail.com" className="btn btn-secondary">問い合わせる</a>
        </article>
      </section>

      <section className="maker-section reveal" data-reveal>
        <div className="maker-media">
          {featuredFarmer ? <img src={featuredFarmer.heroImageUrl} alt={`${featuredFarmer.name}の圃場`} loading="lazy" /> : <img src={heroSlide11} alt="収穫後の野菜" loading="lazy" />}
        </div>
        <div className="maker-copy">
          <p className="eyebrow">People</p>
          <h2>現場が見えると、
            <br />食の信頼は育っていく。</h2>
          <p>
            {featuredFarmer
              ? `${featuredFarmer.name}（${featuredFarmer.prefecture} ${featuredFarmer.city}）のように、現場の実践を丁寧に公開する参加農家が活動しています。`
              : '現場の実践を丁寧に公開する参加農家とともに、食の信頼を積み重ねています。'}
          </p>
          <Link to="/farmers" className="btn btn-secondary">もっと見る</Link>
        </div>
      </section>

      <section className="collab-section reveal" data-reveal>
        <header className="section-head">
          <h2>お知らせ</h2>
        </header>
        <div className="collab-row">
          {loadingPosts && Array.from({ length: 3 }).map((_, idx) => (
            <article className="collab-item skeleton-card" key={`collab-skel-${idx}`} aria-hidden="true" />
          ))}
          {!loadingPosts && collaborationItems.map((item, index) => (
            <article key={item.id} className="collab-item">
              <p className="collab-term">運営からのお知らせ #{index + 1}</p>
              <h3>{item.title || 'タイトル未設定'}</h3>
              {item.date ? <p className="meta-text">{item.date}</p> : null}
              <Link to={item.to} className="text-btn">続きを読む</Link>
            </article>
          ))}
          {!loadingPosts && collaborationItems.length === 0 ? (
            <p>現在は新たなお知らせはありません。</p>
          ) : null}
        </div>
      </section>

      {isDebugMode ? (
        <section className="panel">
          <header className="section-head">
            <h2>Header Debug</h2>
          </header>
          <p className="hint">未ログイン時のヘッダー解決結果をコンソールにも出しています。</p>
          <pre>{JSON.stringify(debugSnapshot, null, 2)}</pre>
        </section>
      ) : null}

      <section className="flow-section reveal" data-reveal>
        <header className="section-head">
          <h2>安心につながる7つの流れ</h2>
        </header>
        <ol className="flow-grid">
          {flowSteps.map((step) => (
            <li key={step.id} className="flow-item reveal" data-reveal>
              <p className="flow-num">#{step.id}</p>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="insta-section reveal" data-reveal>
        <header className="section-head">
          <h2>Instagram</h2>
        </header>
        <InstagramPreview />
      </section>
    </>
  );
}
